import { apiFetch } from './client';

export interface RecordingAdminRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  display_name: string;
  e164: string;
  /** True iff a PIN has been set in the portal. The hash itself is never
   *  exposed to the client. */
  has_pin: boolean;
  verified_at: string | null;
  last_authenticated_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StartVerificationResponse {
  challenge_id: string;
  expires_at: string;
}

export type VerifyResponse =
  | { ok: true }
  | { ok: false; kind: 'no_challenge' | 'expired' | 'wrong' | 'locked'; attemptsLeft?: number };

export const customerRecordingAdmins = {
  list: (customerId: string) =>
    apiFetch<{ rows: RecordingAdminRow[] }>(
      `/api/customer-recording-admins?customerId=${encodeURIComponent(customerId)}`,
    ),

  create: (body: { customer_id: string; display_name: string; e164: string }) =>
    apiFetch<RecordingAdminRow>('/api/customer-recording-admins', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  update: (
    id: string,
    body: {
      display_name?: string;
      new_pin?: string;       // 4-8 digits — replaces the stored PIN hash
      is_active?: boolean;     // manual toggle
    },
  ) =>
    apiFetch<RecordingAdminRow>(`/api/customer-recording-admins/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  remove: (id: string) =>
    apiFetch<void>(`/api/customer-recording-admins/${id}`, { method: 'DELETE' }),

  /** Generate a 4-digit OTP, originate an outbound call to the registered
   *  number, and speak the OTP. The frontend then collects the OTP from
   *  the operator and posts it to `verify` below. */
  startVerification: (id: string) =>
    apiFetch<StartVerificationResponse>(
      `/api/customer-recording-admins/${id}/start-verification`,
      { method: 'POST' },
    ),

  verify: (id: string, pin: string) =>
    apiFetch<VerifyResponse>(`/api/customer-recording-admins/${id}/verify`, {
      method: 'POST',
      body: JSON.stringify({ pin }),
    }),
};
